import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Drawer } from 'antd';

// Shared UI Library Components
import {
  CustomButton,
  InputSearch,
  CustomIcon,
  EIconName,
  SpinElement
} from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './counsellors.scss';

// Components
import { AddCounsellorModal } from '../../modals/add-counsellor-modal/add-counsellor-modal';
import { FiltersList } from '../../shared/components/counsellors/filters-list/filters-list';
import { LogoBlock } from '../../shared/components/logo-block/logo-block';
import { CounsellorsFilters, CounsellorsList } from '../../shared/components/counsellors';

// Store entities
import { getCounsellorsState } from '../../store/counsellors/counsellors.selectors';
import { getUserState } from '../../store/user/user.selectors';
import {
  clearCounsellors,
  loadCounsellors,
  saveCounsellorsFilters,
} from '../../store/counsellors/counsellors.thunks';

// Models
import { ICounsellor } from '../../interfaces/counsellors.interfaces';

type TCounsellorsProps = {}

export const Counsellors: FC<TCounsellorsProps> = (
  {}: TCounsellorsProps
) => {

  const [addModalOpen, setAddModalOpen] = useState<boolean>(false);
  const [filtersMobileOpen, setFiltersMobileOpen] = useState<boolean>(false);

  const {
    counsellors,
    loading,
    filters: storeFilters,
    total,
    total_all
  } = useSelector(getCounsellorsState);
  const { user } = useSelector(getUserState);

  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  const toggleAddCounsellorModal = (): void => {
    setAddModalOpen(prevState => !prevState);
  }

  const toggleFiltersMobileFilters = (): void => {
    setFiltersMobileOpen(prevState => !prevState);
  }

  const fetchCounsellorsList = (): void => {
    dispatch(loadCounsellors({ filters: { offset: 0 }, isFirstLoad: true }));
  }

  const onSearchHandler = (search: string): void => {
    dispatch(saveCounsellorsFilters({ ...storeFilters, search }));
  }

  const onPageChangeHandler = (page: number): void => {
    dispatch(saveCounsellorsFilters({ ...storeFilters, offset: (page - 1) * (storeFilters.limit ?? 10) }));
  }

  const onCounsellorCreatedHandler = (counsellor: ICounsellor): void => {
    navigate(`/counsellors/${ counsellor.id ?? user?.id }`);
  }

  useEffect(() => {
    fetchCounsellorsList();

    return () => {
      dispatch(clearCounsellors());
    };
  }, []);

  return (
    <div className="counsellors">
      <AddCounsellorModal
        open={ addModalOpen }
        onCancel={ toggleAddCounsellorModal }
        onSubmit={ onCounsellorCreatedHandler }
      />

      <div className={ `counsellors--loader counsellors--loader-${ loading ? 'show' : 'hide' }` }>
        <SpinElement fullHeight={ true }/>
      </div>

      <Drawer rootClassName="counsellors-filters-drawer" title={ <LogoBlock/> }
              placement="left"
              onClose={ toggleFiltersMobileFilters } open={ filtersMobileOpen }>
        <CounsellorsFilters/>
      </Drawer>

      <div className="counsellors--filters">
        <CounsellorsFilters openFilters={ filtersMobileOpen }/>
      </div>

      <div className="counsellors--body">
        <div className="body-header">
          <div className="header--title">
          Consultants
          </div>
          <div className="header--action">
            <CustomButton text={ 'Consultant' }
                          size={ 'small' }
                          icon={ <CustomIcon name={ EIconName.AddCircle } size={'size-sm'}/> }
                          iconPosition={ 'left' }
                          onClick={ toggleAddCounsellorModal }
            />
            <CustomIcon className={ 'filter-icon' } name={ EIconName.Tune } onClick={ toggleFiltersMobileFilters }/>
          </div>
        </div>
        { (!!total_all) && <div className="body-search">
          <div className="header--title">
            <span className="bold-text">{ total }</span> Consultants
          </div>
          <div className="header--action">
            <InputSearch placeholder={ 'Search by Name' } onDebounceChange={ onSearchHandler } loading={ loading }/>
          </div>
        </div> }

        <FiltersList/>

        { !!counsellors.length && <div className="body-content">
          <CounsellorsList stripeConnected={ !!user?.is_stripe_connected }
                           counsellors={ counsellors }
                           total={ total }
                           pageChange={ onPageChangeHandler }
          />
        </div> }

        { (!!total_all && !loading && !counsellors.length) && <div className="body-empty">
          <div className="body-empty--title title-center">
            No data to display
          </div>
        </div> }

        { (!total_all && !loading) && <div className="body-empty">
          <div className="body-empty--title">
            As of now, there are no Consultants listed in the system.
          </div>
          <div className="body-empty--subtitle">
            Would you like to add some? Simply click the "Add Consultant" button to begin the process of enlisting Consultants into the application.
          </div>
          <div className="body-empty--subtitle">
            Once added, it's important to complete the profile for each Consultant. After that, don't forget to activate it. This step is crucial for making Consultants available to end Clients. Remember, a Consultant who is not activated will not be visible to your Clients.
          </div>
          <div className="body-empty--subtitle">
            By following these steps, you can efficiently populate and manage your Consultants section.
          </div>
        </div> }
      </div>
    </div>
  );
}
